import React, { useState, useEffect, useRef } from 'react';
import { appendCacheBuster } from '../utils/mobileStyleUtils';

// Format bytes helper
const formatBytes = (bytes, decimals = 1) => {
  if (!bytes || bytes === 0) return '0 B';
  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
};

// Neutral Outline SVG Icons
const Icons = {
  Close: ({ size = 18 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  ),
  Search: ({ size = 16 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="11" cy="11" r="8" />
      <line x1="21" y1="21" x2="16.65" y2="16.65" />
    </svg>
  ),
  Upload: ({ size = 18 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      <polyline points="17 8 12 3 7 8" />
      <line x1="12" y1="3" x2="12" y2="15" />
    </svg>
  ),
  Image: ({ size = 20 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
      <circle cx="8.5" cy="8.5" r="1.5" />
      <polyline points="21 15 16 10 5 21" />
    </svg>
  ),
  Check: ({ size = 14 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="20 6 9 17 4 12" />
    </svg>
  )
};

export default function AssetSelectorModal({
  isOpen,
  onClose,
  onSelect,
  title = "Select Asset",
  currentValue = "",
  tenantId = null,
  user = null,
  assetType = "image", // 'image' | 'video' | 'all'
  cacheBusterKey = null
}) {
  const token = sessionStorage.getItem('token') || localStorage.getItem('token');
  const isDev = window.location.port === '3000';
  const API_BASE = isDev ? `http://${window.location.hostname}:8000` : '';

  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadStatusText, setUploadStatusText] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedAsset, setSelectedAsset] = useState(null);
  const [error, setError] = useState('');
  const [isDragActive, setIsDragActive] = useState(false);

  const fileInputRef = useRef(null);

  // Fetch tenant uploaded assets
  const fetchAssets = () => {
    setLoading(true);
    setError('');

    let url = `${API_BASE}/api/upload?`;
    const params = new URLSearchParams();
    if (tenantId) {
      params.append('tenant_id', tenantId.toString());
    }
    if (cacheBusterKey) {
      params.append('_cb', cacheBusterKey.toString());
    }
    url += params.toString();

    const headers = {
      'X-Tenant-Domain': window.location.hostname
    };
    if (cacheBusterKey) {
      headers['X-Cache-Buster'] = 'true';
    }
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    fetch(url, { headers })
      .then(res => {
        if (!res.ok) throw new Error("Failed to fetch assets list");
        return res.json();
      })
      .then(data => {
        // Filter assets according to assetType
        const filtered = (data || []).filter(a => {
          const mime = (a.mime_type || '').toLowerCase();
          const name = (a.original_name || a.filename || '').toLowerCase();
          if (assetType === 'video') {
            return (
              mime.startsWith('video/') ||
              name.endsWith('.mp4') ||
              name.endsWith('.webm') ||
              name.endsWith('.mov') ||
              name.endsWith('.ogg')
            );
          }
          if (assetType === 'all') return true;
          // default: image / vector formats
          return (
            mime.startsWith('image/') ||
            name.endsWith('.png') ||
            name.endsWith('.jpg') ||
            name.endsWith('.jpeg') ||
            name.endsWith('.svg') ||
            name.endsWith('.webp') ||
            name.endsWith('.gif')
          );
        });
        setAssets(filtered);
        setLoading(false);

        // Auto-select current value if matched
        if (currentValue && !selectedAsset) {
          const matched = filtered.find(a => {
            const loc = a.location || '';
            return currentValue.includes(loc) || (a.filename && currentValue.includes(a.filename));
          });
          if (matched) {
            setSelectedAsset(matched);
          }
        }
      })
      .catch(err => {
        setError(err.message || 'Error loading assets');
        setLoading(false);
      });
  };

  useEffect(() => {
    if (isOpen) {
      fetchAssets();
      setSearchQuery('');
      setSelectedAsset(null);
      setError('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, tenantId]);

  // Handle escape key
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  // Upload handler
  const handleUploadFiles = async (files) => {
    if (!files || files.length === 0) return;
    const file = files[0];

    // Check file type
    if (assetType === 'video') {
      const isVid = file.type.startsWith('video/') || file.name.match(/\.(mp4|webm|mov|ogg)$/i);
      if (!isVid) {
        setError('Please select a video file (MP4, WebM, MOV, OGG).');
        return;
      }
    } else if (assetType !== 'all') {
      if (!file.type.startsWith('image/') && !file.name.endsWith('.svg')) {
        setError('Please select an image file (PNG, JPG, SVG, WebP, GIF).');
        return;
      }
    }

    // For large files (> 15 MB), use chunked upload to bypass Cloud Run 32 MB request size limit
    if (file.size > 15 * 1024 * 1024) {
      await handleChunkedUpload(file);
      return;
    }

    setUploading(true);
    setUploadStatusText('Uploading...');
    setError('');

    const formData = new FormData();
    formData.append('file', file);

    let url = `${API_BASE}/api/upload?`;
    const params = new URLSearchParams();
    if (tenantId) {
      params.append('tenant_id', tenantId.toString());
    }
    url += params.toString();

    try {
      const headers = {
        'X-Tenant-Domain': window.location.hostname
      };
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const res = await fetch(url, {
        method: 'POST',
        headers,
        body: formData
      });

      if (res.status === 413) {
        // Automatically fallback to chunked upload if Cloud Run or proxy returns 413
        await handleChunkedUpload(file);
        return;
      }

      const text = await res.text();
      let data = null;
      try {
        data = JSON.parse(text);
      } catch (e) {
        throw new Error(res.ok ? "Server returned unexpected response" : `Upload failed with HTTP ${res.status}`);
      }

      if (!res.ok) throw new Error(data?.detail || "Upload failed");

      // Format uploaded file item
      const newAsset = {
        id: data.file.id,
        filename: data.file.filename,
        original_name: data.file.original_name,
        location: data.file.location,
        mime_type: data.file.mime_type,
        file_size: data.file.file_size
      };

      setAssets(prev => [newAsset, ...prev]);
      setSelectedAsset(newAsset);
    } catch (err) {
      setError(err.message || 'Failed to upload asset');
    } finally {
      setUploading(false);
      setUploadStatusText('');
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  // Chunked upload handler for large assets (e.g. video files)
  const handleChunkedUpload = async (file) => {
    setUploading(true);
    setError('');

    const CHUNK_SIZE = 8 * 1024 * 1024; // 8 MB chunks (well below Cloud Run 32 MB limit)
    const totalChunks = Math.ceil(file.size / CHUNK_SIZE);
    const uploadId = `${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;

    try {
      let finalData = null;
      for (let chunkIndex = 0; chunkIndex < totalChunks; chunkIndex++) {
        const start = chunkIndex * CHUNK_SIZE;
        const end = Math.min(start + CHUNK_SIZE, file.size);
        const chunkBlob = file.slice(start, end);
        const percent = Math.round((chunkIndex / totalChunks) * 100);

        setUploadStatusText(`Uploading chunk ${chunkIndex + 1} of ${totalChunks} (${percent}%)...`);

        const chunkFormData = new FormData();
        chunkFormData.append('chunk', chunkBlob, file.name);
        chunkFormData.append('upload_id', uploadId);
        chunkFormData.append('chunk_index', chunkIndex.toString());
        chunkFormData.append('total_chunks', totalChunks.toString());
        chunkFormData.append('filename', file.name);
        if (file.type) chunkFormData.append('content_type', file.type);
        if (tenantId) chunkFormData.append('tenant_id', tenantId.toString());

        const headers = {
          'X-Tenant-Domain': window.location.hostname
        };
        if (token) {
          headers['Authorization'] = `Bearer ${token}`;
        }

        const res = await fetch(`${API_BASE}/api/upload/chunk`, {
          method: 'POST',
          headers,
          body: chunkFormData
        });

        const text = await res.text();
        let chunkResult = null;
        try {
          chunkResult = JSON.parse(text);
        } catch (e) {
          throw new Error(res.ok ? "Server returned unexpected response" : `Upload failed with HTTP ${res.status}`);
        }

        if (!res.ok) {
          throw new Error(chunkResult?.detail || `Upload failed at chunk ${chunkIndex + 1} (${res.status})`);
        }

        if (chunkResult.completed && chunkResult.file) {
          finalData = chunkResult;
        }
      }

      setUploadStatusText('Processing and optimizing video...');

      if (finalData && finalData.file) {
        const newAsset = {
          id: finalData.file.id,
          filename: finalData.file.filename,
          original_name: finalData.file.original_name,
          location: finalData.file.location,
          mime_type: finalData.file.mime_type,
          file_size: finalData.file.file_size
        };

        setAssets(prev => [newAsset, ...prev]);
        setSelectedAsset(newAsset);
      } else {
        await fetchAssets();
      }
    } catch (err) {
      console.error("Chunked upload error:", err);
      setError(err.message || 'Failed to upload large file in chunks');
    } finally {
      setUploading(false);
      setUploadStatusText('');
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleConfirmSelect = () => {
    if (!selectedAsset) return;
    const path = selectedAsset.location || `/uploads/${selectedAsset.filename}`;
    const fullyQualifiedUrl = path.startsWith('http') ? path : `${API_BASE}${path}`;
    onSelect(fullyQualifiedUrl, selectedAsset);
    onClose();
  };

  const handleDoubleClickAsset = (asset) => {
    const path = asset.location || `/uploads/${asset.filename}`;
    const fullyQualifiedUrl = path.startsWith('http') ? path : `${API_BASE}${path}`;
    onSelect(fullyQualifiedUrl, asset);
    onClose();
  };

  if (!isOpen) return null;

  const filteredAssets = assets.filter(a => {
    if (!searchQuery.trim()) return true;
    const q = searchQuery.toLowerCase();
    const name = (a.original_name || a.filename || '').toLowerCase();
    return name.includes(q);
  });

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={title}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.65)',
        backdropFilter: 'blur(4px)',
        zIndex: 9999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px',
        boxSizing: 'border-box'
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        style={{
          background: 'var(--card-bg, #1e293b)',
          border: '1px solid var(--border-color, #334155)',
          borderRadius: '12px',
          width: '100%',
          maxWidth: '760px',
          maxHeight: '85vh',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.5), 0 8px 10px -6px rgba(0, 0, 0, 0.5)',
          overflow: 'hidden',
          color: 'var(--text-main, #f8fafc)'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Modal Header */}
        <div
          style={{
            padding: '16px 20px',
            borderBottom: '1px solid var(--border-color, #334155)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            background: 'rgba(0, 0, 0, 0.1)'
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <Icons.Image size={20} />
            <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 700, color: 'var(--primary-color, #38bdf8)' }}>
              {title}
            </h3>
            <span
              style={{
                fontSize: '11px',
                fontWeight: 600,
                color: 'var(--text-mute, #94a3b8)',
                background: 'rgba(255, 255, 255, 0.08)',
                padding: '2px 8px',
                borderRadius: '12px'
              }}
            >
              {filteredAssets.length} {assetType === 'video' ? (filteredAssets.length === 1 ? 'video' : 'videos') : (filteredAssets.length === 1 ? 'image' : 'images')}
            </span>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close modal"
            style={{
              background: 'transparent',
              border: 'none',
              color: 'var(--text-mute, #94a3b8)',
              cursor: 'pointer',
              padding: '6px',
              borderRadius: '6px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'color 0.15s ease'
            }}
            onMouseEnter={(e) => (e.currentTarget.style.color = 'var(--text-main, #fff)')}
            onMouseLeave={(e) => (e.currentTarget.style.color = 'var(--text-mute, #94a3b8)')}
          >
            <Icons.Close size={18} />
          </button>
        </div>

        {/* Modal Toolbar: Search & Upload */}
        <div
          style={{
            padding: '14px 20px',
            borderBottom: '1px solid var(--border-color, #334155)',
            display: 'flex',
            gap: '12px',
            alignItems: 'center',
            flexWrap: 'wrap',
            background: 'rgba(0, 0, 0, 0.04)'
          }}
        >
          {/* Search Box */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              flex: 1,
              minWidth: '220px',
              backgroundColor: 'var(--input-bg, #0f172a)',
              border: '1px solid var(--input-border, #334155)',
              borderRadius: '8px',
              padding: '8px 12px'
            }}
          >
            <span style={{ color: 'var(--text-mute, #94a3b8)', display: 'flex', alignItems: 'center' }}>
              <Icons.Search size={15} />
            </span>
            <input
              type="text"
              placeholder="Search images by name..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{
                background: 'transparent',
                border: 'none',
                color: 'var(--input-text, #f8fafc)',
                fontSize: '13px',
                outline: 'none',
                width: '100%'
              }}
            />
            {searchQuery && (
              <button
                type="button"
                onClick={() => setSearchQuery('')}
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: 'var(--text-mute, #94a3b8)',
                  cursor: 'pointer',
                  padding: 0,
                  fontSize: '12px'
                }}
              >
                ✕
              </button>
            )}
          </div>

          {/* Upload Button & Hidden Input */}
          <input
            ref={fileInputRef}
            type="file"
            accept={assetType === 'video' ? 'video/mp4,video/webm,video/ogg,video/quicktime,.mp4,.webm,.mov' : (assetType === 'all' ? '*/*' : 'image/*,.svg')}
            style={{ display: 'none' }}
            onChange={(e) => handleUploadFiles(e.target.files)}
          />
          <button
            type="button"
            disabled={uploading}
            onClick={() => fileInputRef.current && fileInputRef.current.click()}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              backgroundColor: 'var(--primary-color, #38bdf8)',
              color: '#ffffff',
              border: 'none',
              borderRadius: '8px',
              padding: '8px 16px',
              fontSize: '13px',
              fontWeight: 600,
              cursor: uploading ? 'not-allowed' : 'pointer',
              opacity: uploading ? 0.7 : 1,
              whiteSpace: 'nowrap'
            }}
          >
            <Icons.Upload size={15} />
            <span>{uploading ? (uploadStatusText || 'Uploading...') : 'Upload New Asset'}</span>
          </button>
        </div>

        {/* Error Alert */}
        {error && (
          <div
            style={{
              padding: '10px 20px',
              backgroundColor: 'rgba(239, 68, 68, 0.15)',
              borderBottom: '1px solid rgba(239, 68, 68, 0.3)',
              color: '#ef4444',
              fontSize: '12px',
              fontWeight: 500
            }}
          >
            {error}
          </div>
        )}

        {/* Modal Body: Asset Grid */}
        <div
          onDragOver={(e) => {
            e.preventDefault();
            setIsDragActive(true);
          }}
          onDragLeave={() => setIsDragActive(false)}
          onDrop={(e) => {
            e.preventDefault();
            setIsDragActive(false);
            if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
              handleUploadFiles(e.dataTransfer.files);
            }
          }}
          style={{
            flex: 1,
            overflowY: 'auto',
            padding: '20px',
            minHeight: '260px',
            maxHeight: '440px',
            position: 'relative',
            backgroundColor: isDragActive ? 'rgba(56, 189, 248, 0.05)' : 'transparent',
            outline: isDragActive ? '2px dashed var(--primary-color, #38bdf8)' : 'none',
            outlineOffset: '-6px'
          }}
        >
          {loading ? (
            <div style={{ textAlign: 'center', padding: '60px 20px', color: 'var(--text-mute, #94a3b8)' }}>
              <div style={{ fontSize: '14px', fontWeight: 600 }}>Loading image assets...</div>
            </div>
          ) : filteredAssets.length === 0 ? (
            <div
              style={{
                textAlign: 'center',
                padding: '50px 20px',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '12px'
              }}
            >
              <div
                style={{
                  width: '54px',
                  height: '54px',
                  borderRadius: '50%',
                  background: 'rgba(255, 255, 255, 0.05)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'var(--text-mute, #94a3b8)'
                }}
              >
                <Icons.Image size={28} />
              </div>
              <div>
                <div style={{ fontSize: '14px', fontWeight: 600, color: 'var(--text-main, #f8fafc)' }}>
                  {searchQuery ? 'No matching image assets found' : 'No image assets found'}
                </div>
                <div style={{ fontSize: '12px', color: 'var(--text-mute, #94a3b8)', marginTop: '4px' }}>
                  {searchQuery ? 'Try a different search term or upload a new image.' : 'Upload an image above or drag and drop files here.'}
                </div>
              </div>
              <button
                type="button"
                onClick={() => fileInputRef.current && fileInputRef.current.click()}
                style={{
                  marginTop: '6px',
                  backgroundColor: 'transparent',
                  border: '1px solid var(--border-color, #475569)',
                  color: 'var(--text-main, #f8fafc)',
                  borderRadius: '6px',
                  padding: '6px 14px',
                  fontSize: '12px',
                  fontWeight: 600,
                  cursor: 'pointer'
                }}
              >
                Browse File
              </button>
            </div>
          ) : (
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(130px, 1fr))',
                gap: '14px'
              }}
            >
              {filteredAssets.map(asset => {
                const isSelected = selectedAsset?.id === asset.id;
                const path = asset.location || `/uploads/${asset.filename}`;
                const rawUrl = path.startsWith('http') ? path : `${API_BASE}${path}`;
                const assetUrl = cacheBusterKey ? appendCacheBuster(rawUrl, cacheBusterKey) : rawUrl;
                const displayName = asset.original_name || asset.filename || 'Asset';

                return (
                  <div
                    key={asset.id}
                    onClick={() => setSelectedAsset(asset)}
                    onDoubleClick={() => handleDoubleClickAsset(asset)}
                    title={`${displayName} (Double click to select)`}
                    style={{
                      position: 'relative',
                      display: 'flex',
                      flexDirection: 'column',
                      borderRadius: '8px',
                      border: isSelected
                        ? '2px solid var(--primary-color, #38bdf8)'
                        : '1px solid var(--border-color, rgba(255, 255, 255, 0.12))',
                      backgroundColor: isSelected
                        ? 'rgba(56, 189, 248, 0.1)'
                        : 'rgba(255, 255, 255, 0.03)',
                      boxShadow: isSelected ? '0 0 0 2px rgba(56, 189, 248, 0.25)' : 'none',
                      cursor: 'pointer',
                      overflow: 'hidden',
                      transition: 'all 0.15s ease',
                      userSelect: 'none'
                    }}
                  >
                    {/* Selected Badge */}
                    {isSelected && (
                      <div
                        style={{
                          position: 'absolute',
                          top: '6px',
                          right: '6px',
                          backgroundColor: 'var(--primary-color, #38bdf8)',
                          color: '#ffffff',
                          borderRadius: '50%',
                          width: '20px',
                          height: '20px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          zIndex: 2,
                          boxShadow: '0 2px 4px rgba(0,0,0,0.3)'
                        }}
                      >
                        <Icons.Check size={12} />
                      </div>
                    )}

                    {/* Thumbnail Image/Video Container with subtle transparency checkerboard */}
                    <div
                      style={{
                        height: '96px',
                        width: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: '#0f172a',
                        backgroundImage:
                          'linear-gradient(45deg, #1e293b 25%, transparent 25%), linear-gradient(-45deg, #1e293b 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #1e293b 75%), linear-gradient(-45deg, transparent 75%, #1e293b 75%)',
                        backgroundSize: '12px 12px',
                        backgroundPosition: '0 0, 0 6px, 6px -6px, -6px 0px',
                        overflow: 'hidden',
                        padding: '6px',
                        boxSizing: 'border-box',
                        position: 'relative'
                      }}
                    >
                      {((asset.mime_type || '').startsWith('video/') || (asset.filename || '').match(/\.(mp4|webm|mov|ogg)$/i)) ? (
                        <div style={{ position: 'relative', width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <video
                            src={assetUrl}
                            muted
                            preload="metadata"
                            style={{
                              maxWidth: '100%',
                              maxHeight: '100%',
                              objectFit: 'contain'
                            }}
                          />
                          <div style={{
                            position: 'absolute',
                            width: '28px',
                            height: '28px',
                            borderRadius: '50%',
                            backgroundColor: 'rgba(0, 0, 0, 0.7)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: '#fff',
                            boxShadow: '0 2px 6px rgba(0,0,0,0.5)'
                          }}>
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
                              <polygon points="5 3 19 12 5 21 5 3" />
                            </svg>
                          </div>
                        </div>
                      ) : (
                        <img
                          src={assetUrl}
                          alt={displayName}
                          loading="lazy"
                          style={{
                            maxWidth: '100%',
                            maxHeight: '100%',
                            objectFit: 'contain'
                          }}
                          onError={(e) => {
                            e.currentTarget.style.display = 'none';
                          }}
                        />
                      )}
                    </div>

                    {/* Caption & Metadata */}
                    <div
                      style={{
                        padding: '8px 10px',
                        borderTop: '1px solid var(--border-color, rgba(255, 255, 255, 0.08))',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '2px'
                      }}
                    >
                      <div
                        style={{
                          fontSize: '11px',
                          fontWeight: 600,
                          color: isSelected ? 'var(--primary-color, #38bdf8)' : 'var(--text-main, #f8fafc)',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis'
                        }}
                      >
                        {displayName}
                      </div>
                      <div
                        style={{
                          fontSize: '10px',
                          color: 'var(--text-mute, #94a3b8)',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis'
                        }}
                      >
                        {asset.file_size ? formatBytes(asset.file_size) : ''}
                        {asset.width && asset.height ? ` • ${asset.width}x${asset.height}` : ''}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Modal Footer */}
        <div
          style={{
            padding: '14px 20px',
            borderTop: '1px solid var(--border-color, #334155)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            background: 'rgba(0, 0, 0, 0.1)'
          }}
        >
          <div style={{ fontSize: '12px', color: 'var(--text-mute, #94a3b8)', minWidth: 0, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {selectedAsset ? (
              <span>
                Selected: <strong style={{ color: 'var(--text-main, #fff)' }}>{selectedAsset.original_name || selectedAsset.filename}</strong>
              </span>
            ) : (
              <span>Click an image to select, or double-click to confirm</span>
            )}
          </div>
          <div style={{ display: 'flex', gap: '10px', flexShrink: 0 }}>
            <button
              type="button"
              onClick={onClose}
              style={{
                backgroundColor: 'transparent',
                border: '1px solid var(--border-color, #475569)',
                color: 'var(--text-main, #f8fafc)',
                borderRadius: '8px',
                padding: '8px 16px',
                fontSize: '13px',
                fontWeight: 600,
                cursor: 'pointer'
              }}
            >
              Cancel
            </button>
            <button
              type="button"
              disabled={!selectedAsset}
              onClick={handleConfirmSelect}
              style={{
                backgroundColor: selectedAsset ? 'var(--primary-color, #38bdf8)' : 'rgba(255, 255, 255, 0.1)',
                color: selectedAsset ? '#ffffff' : 'rgba(255, 255, 255, 0.4)',
                border: 'none',
                borderRadius: '8px',
                padding: '8px 18px',
                fontSize: '13px',
                fontWeight: 600,
                cursor: selectedAsset ? 'pointer' : 'not-allowed',
                boxShadow: selectedAsset ? '0 2px 4px rgba(0, 0, 0, 0.2)' : 'none'
              }}
            >
              Choose Selected Asset
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
